import { GoogleOutlined, MailOutlined } from '@ant-design/icons';
import { Button, Form, Input, Space, Typography } from 'antd';
import type React from 'react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { firebaseAuth } from '@/core/providers/AuthFirebaseProvider';

const { Title, Text } = Typography;

interface FirebaseAuthFormProps {
  onSuccess?: () => void;
  onError?: (error: string) => void;
}

export const FirebaseAuthForm: React.FC<FirebaseAuthFormProps> = ({ onSuccess, onError }) => {
  const [form] = Form.useForm<{ email: string; password: string }>();
  const [loading, setLoading] = useState(false);
  const [emailLoading, setEmailLoading] = useState(false);

  const handleGoogleSignIn = async () => {
    try {
      setLoading(true);
      const provider = new firebaseAuth.GoogleAuthProvider();
      await firebaseAuth.signInWithPopup(provider);
      onSuccess?.();
    } catch (error) {
      console.error('Google sign-in error:', error);
      onError?.(error instanceof Error ? error.message : 'Google sign-in failed');
    } finally {
      setLoading(false);
    }
  };

  const handleEmailSignIn = async (values: { email: string; password: string }) => {
    try {
      setEmailLoading(true);
      await firebaseAuth.signInWithEmailAndPassword(values.email, values.password);
      onSuccess?.();
    } catch (error) {
      console.error('Email sign-in error:', error);
      onError?.(error instanceof Error ? error.message : 'Email sign-in failed');
    } finally {
      setEmailLoading(false);
    }
  };

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <div style={{ textAlign: 'center' }}>
        <Title level={3}>Sign In</Title>
        <Text type="secondary">Choose your preferred sign-in method</Text>
      </div>

      <Button
        type="primary"
        icon={<GoogleOutlined />}
        size="large"
        block
        loading={loading}
        onClick={handleGoogleSignIn}
      >
        Continue with Google
      </Button>

      <div style={{ textAlign: 'center' }}>
        <Text type="secondary">or</Text>
      </div>

      <Form
        form={form}
        name="email-signin"
        onFinish={handleEmailSignIn}
        layout="vertical"
        size="large"
      >
        <Form.Item
          className="app-form-item"
          name="email"
          rules={[
            { required: true, message: 'Please input your email!' },
            { type: 'email', message: 'Please enter a valid email!' },
          ]}
        >
          <Input prefix={<MailOutlined />} placeholder="Email" type="email" />
        </Form.Item>

        <Form.Item
          className="app-form-item"
          name="password"
          rules={[{ required: true, message: 'Please input your password!' }]}
        >
          <Input.Password placeholder="Password" />
        </Form.Item>

        <Form.Item>
          <Button
            type="primary"
            htmlType="submit"
            block
            loading={emailLoading}
            icon={<MailOutlined />}
          >
            Sign in with Email
          </Button>
        </Form.Item>

        <Form.Item style={{ marginBottom: 0, marginTop: -12, textAlign: 'right' }}>
          <Link to="/forgot-password">Forgotten password?</Link>
        </Form.Item>
      </Form>
    </Space>
  );
};
